import type { RuleResource } from '@/features/legends/api/resources/rules'
import type { LegacyIdFixer } from '@/features/legends/editor/legacy-ids'
import {
  mapUnknownResourceToElement,
  UnknownElementError,
} from '@/features/legends/editor/legend-document'
import { isNotNil } from '@/utils/object-util'
import type { Rule } from './rules'

/**
 * Maps a list of rules, fixing references to legacy question IDs.
 *
 * @param resources
 * @param ids
 */
export function mapRuleResourcesToRules(
  resources: RuleResource[],
  ids: LegacyIdFixer,
): Rule[] {
  // Legends saved by older builders can have `null` in their rule lists.
  return resources
    .filter(isNotNil)
    .map((resource) => mapRuleResourceToRule(resource, ids))
}

/**
 * Maps a rule, fixing references to legacy question IDs.
 *
 * @param resource
 * @param ids
 */
export function mapRuleResourceToRule(
  resource: RuleResource,
  ids: LegacyIdFixer,
): Rule {
  switch (resource.type) {
    case 'BinaryOperation':
      return {
        type: 'BinaryOperation',
        target: ids.resolve(resource.target),
        op: resource.op,
        value: resource.value,
      }
    case 'Conjunction':
      return {
        type: 'Conjunction',
        op: resource.op,
        rules: resource.rules
          ? mapRuleResourcesToRules(resource.rules, ids)
          : undefined,
      }
    case 'Constant':
      return { type: 'Constant', value: resource.value }
    case 'Expression':
      return { type: 'Expression', expr: resource.expr }
    case 'HasSkills':
      return { type: 'HasSkills', op: resource.op, skills: resource.skills }
    case 'If':
      return {
        type: 'If',
        conditionRule:
          resource.conditionRule &&
          mapRuleResourceToRule(resource.conditionRule, ids),
        thenRule:
          resource.thenRule && mapRuleResourceToRule(resource.thenRule, ids),
        elseRule:
          resource.elseRule && mapRuleResourceToRule(resource.elseRule, ids),
      }
  }
  return mapUnknownResourceToElement(resource)
}

/**
 * Maps a list of rules to the API's.
 *
 * @param rules
 */
export function mapRulesToResources(rules: Rule[]): RuleResource[] {
  return rules.map(mapRuleToResource)
}

/**
 * Maps a rule to the API's.
 *
 * @param rule
 */
export function mapRuleToResource(rule: Rule): RuleResource {
  switch (rule.type) {
    case 'BinaryOperation':
      return {
        type: 'BinaryOperation',
        target: rule.target,
        op: rule.op,
        value: rule.value,
      }
    case 'Conjunction':
      return {
        type: 'Conjunction',
        op: rule.op,
        rules: rule.rules && mapRulesToResources(rule.rules),
      }
    case 'Constant':
      return { type: 'Constant', value: rule.value }
    case 'Expression':
      return { type: 'Expression', expr: rule.expr }
    case 'HasSkills':
      return { type: 'HasSkills', skills: rule.skills, op: rule.op }
    case 'If':
      return {
        type: 'If',
        conditionRule:
          rule.conditionRule && mapRuleToResource(rule.conditionRule),
        thenRule: rule.thenRule && mapRuleToResource(rule.thenRule),
        elseRule: rule.elseRule && mapRuleToResource(rule.elseRule),
      }
    case 'Unknown':
      throw new UnknownElementError('filter', rule)
  }
}
